import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Typography } from "@mui/material";
import { DataTable, IconButton, Tooltip } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Download04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { GET_STATEMENTS } from "graphql/statement";
import { formatCashFull } from "utils/formatCash";
import { titleCase } from "title-case";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";

interface Statement {
  id: string;
  accountId: string;
  periodMonth: string;
  periodStart: number;
  periodEnd: number;
  statementDate: number;
  startingBalance?: { amount: number; currency: string };
  endingBalance?: { amount: number; currency: string };
  statementBalance?: { amount: number; currency: string };
  statementUrl?: string | null;
  account?: {
    id: string;
    name?: string;
    last4?: string;
    accountType?: string;
  };
}

interface StatementsTableProps {
  selectedCompanyId?: string;
  searchTerm?: string;
  /** Absent means every program's statements. */
  accountId?: string;
}

const StatementsTable: React.FC<StatementsTableProps> = ({
  searchTerm,
  accountId,
  selectedCompanyId,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { monthFormat, dayFormat } = useMemo(
    () => ({
      monthFormat: new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }),
      dayFormat: new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    }),
    [locale],
  );

  // periodStart/periodEnd are boundary instants in the server's zone, and a
  // cycle statement's need not be a month edge, so the month reads periodMonth.
  const formatMonth = useCallback(
    (periodMonth: string) => {
      const [year, month] = periodMonth.split("-").map(Number);
      if (!year || !month) return periodMonth;
      return monthFormat.format(Date.UTC(year, month - 1, 1));
    },
    [monthFormat],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_STATEMENTS,
      skip: !!selectedCompanyId,
      initialVariables: { first: DEFAULT_PAGINATION_SIZE, accountId },
      extractData: (data) =>
        ((data?.getStatements?.edges ?? []) as { node: Statement }[]).map(
          (edge) => edge.node,
        ),
    });

  const scoped = useCompanyConnection<Statement>({
    query: GET_STATEMENTS,
    variables: { selectedCompanyId, accountId },
    field: "getStatements",
    skip: !selectedCompanyId,
  });
  const statements: Statement[] = selectedCompanyId ? scoped.rows : dataArray;

  const filtered = useMemo(() => {
    if (!searchTerm) return statements;
    const q = searchTerm.toLowerCase();
    return statements.filter((s) => {
      const acct = s.account?.name?.toLowerCase() ?? "";
      const last4 = s.account?.last4 ?? "";
      // The same formatter the Month column uses, so what is on screen is what
      // the search matches.
      const month = formatMonth(s.periodMonth).toLowerCase();
      return (
        acct.includes(q) ||
        last4.includes(q) ||
        month.includes(q) ||
        s.periodMonth.includes(q)
      );
    });
  }, [statements, searchTerm, formatMonth]);

  const formatAccount = (s: Statement) => {
    const name = s.account?.name
      ? titleCase(s.account.name.toLowerCase())
      : t("Account");
    if (!s.account?.last4) return name;
    return `${name} •• ${s.account.last4}`;
  };

  const formatBalance = (value?: { amount: number; currency: string }) => {
    if (!value?.amount && value?.amount !== 0) return "—";
    return formatCashFull(value.amount, value.currency);
  };

  const handleDownload = (s: Statement) => {
    if (!s.statementUrl) return;
    window.open(s.statementUrl, "_blank", "noopener,noreferrer");
  };

  const columns: DataColumn<Statement>[] = useMemo(
    () => [
      {
        id: "month",
        header: t("Month"),
        cell: (statement) => formatMonth(statement.periodMonth),
      },
      {
        id: "account",
        header: t("Account"),
        cell: formatAccount,
      },
      {
        id: "start",
        header: t("Start"),
        cell: (statement) => dayFormat.format(statement.periodStart),
      },
      {
        id: "end",
        header: t("End"),
        cell: (statement) => dayFormat.format(statement.periodEnd),
      },
      {
        id: "openingBalance",
        header: t("Opening Balance"),
        cell: (statement) => formatBalance(statement.startingBalance),
      },
      {
        id: "closingBalance",
        header: t("Closing Balance"),
        cell: (statement) => formatBalance(statement.endingBalance),
      },
      {
        id: "download",
        header: t("Download"),
        cell: (statement) => (
          <Tooltip
            describeChild
            title={
              statement.statementUrl
                ? t("Download statement PDF")
                : t("This statement is not available to download")
            }
          >
            {/* A disabled button emits no pointer events and takes no tab
                stop, so the wrapper carries both. */}
            <Box
              component="span"
              tabIndex={statement.statementUrl ? undefined : 0}
              sx={{ display: "inline-flex" }}
            >
              <IconButton
                size="small"
                variant="ghost"
                disabled={!statement.statementUrl}
                onClick={() => handleDownload(statement)}
                aria-label={t("Download statement PDF")}
              >
                <HugeiconsIcon icon={Download04Icon} size={16} />
              </IconButton>
            </Box>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, formatMonth, dayFormat],
  );

  return (
    <Box ref={scoped.viewportRef} sx={{ height: "100%" }}>
      {selectedCompanyId && scoped.error && (
        <Alert severity="error">
          {t("Statements are unavailable or access is denied")}
        </Alert>
      )}
      <DataTable<Statement>
        columns={columns}
        rows={filtered}
        getRowKey={(statement) => statement.id}
        loading={selectedCompanyId ? scoped.loading : displayLoading}
        isFetchingMore={
          selectedCompanyId ? scoped.isFetchingMore : isInfiniteScrollFetching
        }
        onScroll={selectedCompanyId ? scoped.handleScroll : handleScroll}
        loadingMoreText={t("LoadingMore")}
        emptyState={
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-tertiary)", py: "var(--space-5)" }}
          >
            {searchTerm
              ? t("No statements match your search")
              : t("No statements yet")}
          </Typography>
        }
        stickyHeader
      />
    </Box>
  );
};

export default StatementsTable;
